import { Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, from, map } from 'rxjs';
import { ApiListResponse } from 'src/app/api/api';
import { ApiSearch } from 'src/app/api/services/api-utils.service';
import type { Folder, FolderItem } from 'src/app/api/services/folder';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';

export type FolderTreeItem = {
  id: string;
  name: string;
  parentId?: string;
  children: FolderTreeItem[];
};

@Injectable({
  providedIn: 'root'
})
export class FolderService {

  folderTree: FolderTreeItem[] = [];
  folderMap: Map<string, FolderTreeItem> = new Map();

  constructor(private foldersApiService: FoldersApiService) { }

  async loadFolders(type: string, options: ApiSearch): Promise<FolderItem[]> {
    const {query, includeSubcompanies, sortBy, reverse} = options;
    let nameQuery = '';
    
    if (query) {
      nameQuery = ` AND name:~"${query}"`;
    }
    let folderQuery = `kind:~"${type}"`;
    const items: FolderItem[] = [];
    let cursor: null | string = null;

    do {
      const response = await this.foldersApiService.list({
        query: `${folderQuery}${nameQuery}`.trim(),
        includeSubcompanies,
        sortBy: sortBy,
        reverse: reverse
      }, cursor);
      
      items.push(...response.items.map(item => ({
        ...item,
        isFolder: true
      }) as FolderItem));
      if (!query) {
        this.loadTree(response.items);
      }
      cursor = response.cursor || response.nextPageToken;
    } while (cursor);
    return items;
  }

  loadTree(folders: Folder[]): void {
    this.folderTree = [];

    // First pass: create map of folder items
    folders.forEach(folder => {
      if (folder.id) {
        const folderItem: FolderTreeItem = {
          id: folder.id,
          name: folder.name,
          parentId: folder.parentId,
          children: []
        };
        this.folderMap.set(folder.id, folderItem);
      }
    });

    // Second pass: build tree structure
    for (const folderItem of this.folderMap.values()) {
      if (!folderItem.parentId) {
        this.folderTree.push(folderItem);
      } else {
        const parentItem = this.folderMap.get(folderItem.parentId);
        if (parentItem) {
          parentItem.children.push(folderItem);
        } else {
          this.folderTree.push(folderItem);
        }
      }
    }
  }

  deleteFolder(folderId: string) {
    return this.foldersApiService.delete(folderId);
  }

  showFolderItem(folderId: string | null | undefined, itemFolderId: string | null | undefined): boolean {
    return !folderId && !itemFolderId || folderId === itemFolderId;
  }

  getItemsFromFolderAndSubfolders<T extends {id?: string}>(
    folderId: string,
    service: HasFolderStructure<T>
  ): Promise<T[]> {
    const subfolders = this.getSubfoldersIds(
      this.findFolderRecursive(this.folderTree, folderId),
      []
    );
    const folders = [
      folderId,
      ...subfolders
    ];

    return firstValueFrom(
      forkJoin(
        folders.map(folderId => from(service.listByFolderId(folderId)))
      ).pipe(
        map(responses => responses.flatMap(response => response.items))
      )
    );
  }

  private getSubfoldersIds(currentFolder: FolderTreeItem, folders: string[]): string[] {
    currentFolder.children.forEach(child => {
      folders.push(child.id);
      if (child.children.length > 0) {
        this.getSubfoldersIds(child, folders);
      }
    });
    return folders;
  }

  private findFolderRecursive(folders: FolderTreeItem[], folderId: string): FolderTreeItem | null {
    for (const folder of folders) {
      if (folder.id === folderId) {
        return folder;
      }
      const found = this.findFolderRecursive(folder.children, folderId);
      if (found) {
        return found;
      }
    }
    return null;
  }
}

export interface HasFolderStructure<T> {
  listByFolderId: (folderId: string) => Promise<ApiListResponse<T>>;
}
